import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FiLogOut, FiMenu, FiHome, FiSettings, FiSearch } from 'react-icons/fi';

import PriceForm from '../components/PriceFormClean';
import AdminPanel from '../components/AdminPanel';
import { getListStations, getStationDetail, logout as apiLogout } from '../api_controll/apiClient';
import { logoutUser } from '../redux/userSlice';
import { can } from '../auth/can';
import { PERMS } from '../auth/permissions';
import  '../styles/landing-page.css';

export default function LandingPage() {
  const [stations, setStations] = useState([]);
  const [selectedStationId, setSelectedStationId] = useState(null);
  const [stationDetail, setStationDetail] = useState(null);

  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailError, setDetailError] = useState(null);

  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const [stationSearch, setStationSearch] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(state => state.user.user);

  const formatPriceChangeDate = (value) => {
  if (!value) return 'Bez změny';

  const match = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?([+-]\d{2})(?::?(\d{2}))?$/
  );

  if (!match) return value;

  const [
    ,
    year,
    month,
    day,
    hour,
    minute,
    second,
    fraction = '0',
    tzHour,
    tzMinute = '00',
  ] = match;

  const milliseconds = fraction.padEnd(3, '0').slice(0, 3);

  const isoDate = `${year}-${month}-${day}T${hour}:${minute}:${second}.${milliseconds}${tzHour}:${tzMinute}`;
  const date = new Date(isoDate);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
  const canSeeAdminPanel = useMemo(
    () => can(user, PERMS.ADMIN_PANEL_VIEW),
    [user]
  );

  const fetchStationsList = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const list = await getListStations();
      setStations(list ?? []);
    } catch (err) {
      console.error(err);
      setError('Chyba při načítání seznamu čerpacích stanic');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchStationDetail = useCallback(async (stationId) => {
    if (!stationId) return;

    try {
      setDetailLoading(true);
      setDetailError(null);
      const detail = await getStationDetail(stationId);

      if (!detail) {
        setStationDetail(null);
        setDetailError('Stanice nebyla nalezena.');
        return;
      }

      setStationDetail(detail);
    } catch (err) {
      console.error(err);
      setDetailError('Chyba při načítání detailu stanice');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStationsList();
  }, [fetchStationsList]);

  useEffect(() => {
    if (!selectedStationId) {
      setStationDetail(null);
      setDetailError(null);
      setDetailLoading(false);
      return;
    }

    fetchStationDetail(selectedStationId);
  }, [selectedStationId, fetchStationDetail]);

  const handleLogout = async () => {
    try {
      await apiLogout();
    } catch (e) {
      console.error('Logout API failed:', e);
    }
    dispatch(logoutUser());
    navigate('/');
  };

  const handlePricesSaved = useCallback(async () => {
    await fetchStationsList();
    if (selectedStationId) {
      await fetchStationDetail(selectedStationId);
    }
  }, [fetchStationsList, fetchStationDetail, selectedStationId]);

  const filteredStations = useMemo(() => {
    const q = stationSearch.trim().toLowerCase();
    if (!q) return stations;

    return stations.filter(station =>
      `${station.name} ${station.id}`.toLowerCase().includes(q)
    );
  }, [stations, stationSearch]);

  const selectedStation = stations.find(s => s.id === selectedStationId);

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="topbar-left">
          <button
            className="icon-button mobile-only"
            onClick={() => setSidebarOpen(prev => !prev)}
            aria-label="Otevřít menu"
          >
            <FiMenu />
          </button>

          <div className="brand-block">
            <strong>Armex</strong>
            <span className="brand-subtitle">Správa cen</span>
          </div>
        </div>

        <div className="topbar-center">
          {selectedStation ? (
            <div className="selected-station-pill">
              <strong>{selectedStation.name}</strong>
              <span>{formatPriceChangeDate(selectedStation.latest_price_change_at)}</span>            
              </div>
          ) : (
            <div className="selected-station-pill muted">
              Není vybrána žádná stanice
            </div>
          )}
        </div>

        <div className="topbar-right">
          <div className="user-chip">
            <span>{user?.username}</span>
            <small>{user?.role_name}</small>
          </div>
          <button className="icon-button danger" onClick={handleLogout} aria-label="Odhlásit">
            <FiLogOut />
          </button>
        </div>
      </header>

      <div className="layout-body">
        <aside className={`sidebar ${sidebarOpen ? 'open' : 'collapsed'}`}>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Navigace</div>

            <button
              className={`nav-item ${!selectedStationId && !showAdminPanel ? 'active' : ''}`}
              onClick={() => {
                setSelectedStationId(null);
                setShowAdminPanel(false);
              }}
            >
              <FiHome />
              <span>Hlavní stránka</span>
            </button>

            {canSeeAdminPanel && (
              <button
                className={`nav-item ${showAdminPanel ? 'active' : ''}`}
                onClick={() => {
                  setSelectedStationId(null);
                  setShowAdminPanel(true);
                }}
              >
                <FiSettings />
                <span>Admin panel</span>
              </button>
            )}
          </div>

          <div className="sidebar-section">
            <div className="sidebar-section-title">Čerpací stanice</div>

            <div className="search-box">
              <FiSearch />
              <input
                type="text"
                placeholder="Hledat stanici..."
                value={stationSearch}
                onChange={(e) => setStationSearch(e.target.value)}
              />
            </div>

            <div className="station-list">
              {loading ? (
                <div className="sidebar-state">Načítání…</div>
              ) : error ? (
                <div className="sidebar-state error">{error}</div>
              ) : filteredStations.length === 0 ? (
                <div className="sidebar-state">Žádné výsledky</div>
              ) : (
                filteredStations.map((station) => (
                  <button
                    key={station.id}
                    className={`station-item ${selectedStationId === station.id ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedStationId(station.id);
                      setShowAdminPanel(false);
                      if (window.innerWidth < 1024) setSidebarOpen(false);
                    }}
                  >
                    <div className="station-main">
                      <span className="station-name">{station.name}</span>
                          <span>{formatPriceChangeDate(station.latest_price_change_at)}</span>
                    </div>

                    <div className="station-meta">
                      {station.has_unconfirmed && (
                        <span className="meta-dot danger" title="Nepotvrzené změny" />
                      )}
                      {!!station.eur_enabled && (
                        <span className="meta-chip">EUR</span>
                      )}
                      {(station.planned_count ?? 0) > 0 && (
                        <span className="meta-chip warning">{station.planned_count}</span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </aside>

        <main className="content">
          {showAdminPanel ? (
            <AdminPanel />
          ) : selectedStationId ? (
            detailLoading ? (
              <div className="content-state">Načítám detail stanice…</div>
            ) : detailError ? (
              <div className="content-state error">{detailError}</div>
            ) : stationDetail ? (
              <>
            

                <PriceForm
                  key={stationDetail.station?.id ?? stationDetail.id}
                  detail={stationDetail}
                  onPricesSaved={handlePricesSaved}
                />
              </>
            ) : (
              <div className="content-state">Stanice nebyla nalezena.</div>
            )
          ) : (
            <div className="empty-dashboard">
              <h2>Vyberte čerpací stanici</h2>
              <p>Pro pokračování vyberte stanici v levém panelu.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}